import { useMemo } from "react";

import type { MatchEvent } from "../../store/useMatchLogStore";
import type {
  PublicCardEntry,
  PublicCardKind,
  PublicSubstitutionEntry,
} from "../../pages/logger/utils/publicHeaderEvents";
import type { Match } from "../../pages/logger/types";
import {
  CANONICAL_START_SECONDS,
  EXTRA_FIRST_HALF_END_SECONDS,
  EXTRA_SECOND_HALF_END_SECONDS,
  REGULATION_FIRST_HALF_SECONDS,
  REGULATION_SECOND_HALF_SECONDS,
  type PeriodPhase,
} from "../../pages/logger/hooks/usePeriodManager";
import {
  findHalftimeWindowForTimestamp,
  parseTimestampSafe,
  reconstructStoppageElapsedSeconds,
  splitElapsedSecondsIntoMinutes,
} from "../../pages/logger/lib/clockHelpers";
import { formatMatchTime } from "../../lib/utils";
import type {
  PublicShootoutKickEntry,
  ShootoutRoundDescriptor,
} from "../../pages/logger/utils/penaltyShootout";
import { isPenaltyGoal } from "../../pages/logger/utils/penaltyGoal";

/**
 * Center-spine match timeline for the public share page (client design
 * 2026-08-06, reordered + period markers added 2026-08-07, reverted back to
 * newest-first 2026-08-07). Replaces the per-team event lists that used to
 * hang off the score bar: goals, cards and substitutions from both teams now
 * share one chronological column, home on the left of the spine and away on
 * the right, **newest first** — during a live match the newest event lands
 * at the top, where the viewer is already looking, rather than at the
 * bottom of a growing list. (Briefly shipped earliest-first; the client
 * asked for it back — see the sort comment below for the tie-break rule.)
 *
 * Match-wide structural markers (kickoff, half-time, extra-time boundaries,
 * full time) render as centered rows on the spine itself, since they belong
 * to neither team — see `buildBoundaryRows` below for the merge rules.
 *
 * Row-level test hooks (`public-card-{id}`, `data-card-kind`, the card chip
 * classes, `public-sub-{id}`, and the red/emerald off/on labels) are carried
 * over unchanged from the old header so the existing E2E coverage
 * (PMA-COCK-092) keeps asserting the same contract. New boundary rows get
 * their own hooks (`public-timeline-boundary-{kind}`, `data-boundary-kind`).
 */

export type TimelineSide = "home" | "away";

interface GoalRow {
  kind: "goal";
  id: string;
  side: TimelineSide;
  seconds: number;
  period: number;
  label: string;
  minuteLabel: string;
  /**
   * True for a scored penalty (PMA-COCK-195, `isPenaltyGoal`). The visible
   * "(P)" mark itself lives in `label` — `formatGoalScorer`
   * (`useLiveScore.ts`) appends it the same way it already appends " (AG)"
   * for an own goal, so there is exactly one place a scorer suffix renders
   * from. This field only drives the row's `data-penalty` test hook, a
   * locale-independent way to assert the same fact without string-matching
   * the (localized) label text. False for a normal goal and always false
   * for a shootout kick (structurally excluded — see `penaltyGoal.ts`'s doc
   * comment).
   */
  penalty: boolean;
}

interface CardRow {
  kind: "card";
  id: string;
  side: TimelineSide;
  seconds: number;
  period: number;
  entry: PublicCardEntry;
  minuteLabel: string;
}

interface SubRow {
  kind: "sub";
  id: string;
  side: TimelineSide;
  seconds: number;
  period: number;
  entry: PublicSubstitutionEntry;
  minuteLabel: string;
}

export type BoundaryKind =
  | "matchStart"
  | "halftime"
  | "secondHalfEnd"
  | "extraTimeStart"
  | "extraTimeHalftime"
  | "extraTimeEnd"
  | "matchEnd";

interface BoundaryRow {
  kind: "boundary";
  id: BoundaryKind;
  boundaryKind: BoundaryKind;
  seconds: number;
  period: number;
}

type TimelineRow = GoalRow | CardRow | SubRow | BoundaryRow;

interface PublicMatchTimelineProps {
  goals: { home: MatchEvent[]; away: MatchEvent[] };
  cards: { home: PublicCardEntry[]; away: PublicCardEntry[] };
  substitutions: {
    home: PublicSubstitutionEntry[];
    away: PublicSubstitutionEntry[];
  };
  formatGoalScorer: (event: MatchEvent) => string;
  t: (key: string, fallback?: string) => string;
  /**
   * Raw period start/end payload, keyed "1".."4" (extra time uses 3/4) —
   * same shape the cockpit's `usePeriodManager` consumes. Optional so the
   * component still renders (with no boundary rows) for callers that
   * haven't wired it through yet.
   */
  periodTimestamps?: Match["period_timestamps"];
  /**
   * The match's status string (e.g. "Completed", "Live_First_Half"). Only
   * `"Completed"` is treated as "the match has genuinely ended" — gates the
   * `matchEnd` marker so a merely-paused match (e.g. "Fulltime" pending an
   * extra-time decision) never shows a full-time marker prematurely.
   */
  matchStatus?: string;
  /**
   * Current continuous match-clock seconds (same value the page's own
   * broadcast clock is built from — see `computeTimerFormulas`/
   * `computeBroadcastClock`). Used only to position a boundary whose
   * "next" period hasn't started yet (mid-break) or the final whistle once
   * the match is over; never used to derive a period's own start (that
   * always comes from `periodTimestamps`).
   */
  nowSeconds?: number;
  /**
   * The match's `current_period_start_timestamp` — non-empty exactly while
   * a clock is actively running (PMA-COCK-173). Combined with `matchStatus`
   * via `isPeriodLiveAndRunning` below to recognize a period that is
   * genuinely still live even though its `periodTimestamps` entry carries a
   * (stale but internally coherent, `.end > .start`) `.end` — the case left
   * uncovered by PMA-COCK-156's incoherent-window guard, which only catches
   * `.end <= .start`. Dev incident: a mid-period global Stop stamped
   * `period_timestamps.1.end`; the clock was later resumed via the
   * effective/ineffective toggle (`/clock-mode`), which sets this timestamp
   * but never clears `period_timestamps` — so `.end` survived, genuinely
   * after `.start`, while `matchStatus` stayed `Live_First_Half`.
   */
  currentPeriodStartTimestamp?: string | null;
  /**
   * Penalty-shootout kicks (PMA-COCK-164), pre-labeled by
   * `derivePublicShootoutRows`. Rendered as their own block under a
   * "Tanda de penales" divider, ordered by kick sequence — NOT merged into
   * the minute-ordered spine above, since every kick shares the same
   * PENALTIES-phase clock reading and would otherwise stack onto one point.
   * This is the SINGLE public shootout surface: the pre-existing
   * `PublicShootoutBoard` component is no longer mounted on the public page
   * (lead decision, 2026-09-07, closing the duplication this prop's first
   * version introduced) — its file still exists for any other
   * consumer/test. Omitted or empty renders nothing.
   */
  shootoutKicks?: PublicShootoutKickEntry[];
  /**
   * Shootout standing (PMA-PUB-017), threaded down rather than recomputed —
   * the exact same `shootout.home.scored` / `shootout.away.scored` the
   * score-bar plaque's own "Penales X - Y" already shows
   * (`PublicMatchAnalytics.tsx`). Feeds the header chip above the kicks.
   * Defaults to 0/0 so a caller that hasn't threaded these yet (or a test
   * exercising `shootoutKicks` alone) still renders.
   */
  shootoutHomeScore?: number;
  shootoutAwayScore?: number;
  /**
   * PMA-COCK-201: the same round/sudden-death descriptor the cockpit's
   * `PenaltyShootoutPanel` derives from `describeShootoutRound` — passed
   * down rather than recomputed here so the two surfaces read one function's
   * output instead of two independently-maintained copies that could drift
   * (exactly the failure this project already had once, PMA-COCK-164 vs the
   * pre-existing `PublicShootoutBoard`). Only `phase` and `round` are
   * rendered here — the "decided" winner hint is cockpit-only operational
   * information for the person still logging kicks, not something this
   * read-only board needs to show. Omitted, the header shows the score only,
   * unchanged from before this prop existed.
   */
  shootoutRound?: ShootoutRoundDescriptor;
}

const PERIOD_KEYS = ["1", "2", "3", "4"] as const;
type PeriodKey = (typeof PERIOD_KEYS)[number];

const PHASE_BY_PERIOD_KEY: Record<PeriodKey, PeriodPhase> = {
  "1": "FIRST_HALF",
  "2": "SECOND_HALF",
  "3": "FIRST_HALF_EXTRA_TIME",
  "4": "SECOND_HALF_EXTRA_TIME",
};

const BOUNDARY_TRANSITIONS: Array<{
  from: PeriodKey;
  to: PeriodKey;
  boundaryKind: BoundaryKind;
  /**
   * Label to use while `to` hasn't started yet yet the match isn't over.
   * Only the 2→3 gap is genuinely ambiguous (regulation can be followed by
   * extra time OR go straight to penalties/full time — `FULLTIME`'s allowed
   * transitions include both `LIVE_EXTRA_FIRST` and `COMPLETED`/`PENALTIES`),
   * so only it gets a distinct neutral label. 1→2 and 3→4 are deterministic
   * in the state machine (`HALFTIME`/`EXTRA_HALFTIME` only ever lead to the
   * next period), so they keep their normal label even while pending.
   */
  pendingBoundaryKind?: BoundaryKind;
}> = [
  { from: "1", to: "2", boundaryKind: "halftime" },
  {
    from: "2",
    to: "3",
    boundaryKind: "extraTimeStart",
    pendingBoundaryKind: "secondHalfEnd",
  },
  { from: "3", to: "4", boundaryKind: "extraTimeHalftime" },
];

/**
 * Builds the match-wide boundary rows (kickoff, breaks, full time) from the
 * same period-timestamp payload `usePeriodManager` consumes.
 *
 * PMA-COCK-163: every divider sits at its CANONICAL match-clock second
 * (`CANONICAL_START_SECONDS` — 0 / 2700 / 5400 / 6300 — and, for a period's
 * own end, the matching regulation-length constant below) rather than the
 * persisted `period_timestamps[key].global_start_seconds`. That persisted
 * value is `match_time_seconds + ineffective_time_seconds` (effective +
 * ineffective play, `matches_new.py`) — real elapsed wall time, which is
 * exactly what a divider must NOT show: an 8-minute first-half stoppage
 * made the halftime row read "53'" instead of "45'", above events that were
 * themselves correctly stamped in the 45-52' range. A structural divider
 * marks a REGULATION boundary, not an elapsed-time reading — it is always
 * 45'/90'/105'/120' regardless of how much stoppage preceded it.
 *
 * Each row also carries `period` (the number of the block it anchors —
 * matching `resolvePublicEventTiming`'s per-event `period`), since the
 * component's own sort is `(period desc, seconds desc)`, not `seconds`
 * alone: a first-half stoppage event and a second-half event can carry the
 * SAME continuous elapsed seconds (both clocks share one numbering scheme),
 * so `period` is the only thing that can tell them apart and keep each
 * under the correct half's block.
 *
 * Merge rule: a period's end and the next period's start are the same
 * instant on the continuous match clock (the clock pauses for the break, so
 * it resumes at the value it paused at) — so they always render as ONE row,
 * never two, at that shared canonical second.
 *
 * Never guess what comes next. When a period has ended but the next one
 * hasn't started AND the match isn't `"Completed"`, the only fact actually
 * known is that the period ended — so the row uses a neutral "end of X"
 * label (`secondHalfEnd`/`extraTimeEnd`) rather than assuming extra time
 * (most matches never go to extra time) or assuming the match is over
 * (`FULLTIME` can also lead into extra time, or penalties can precede
 * `COMPLETED`). It resolves into the specific label once the next period
 * actually starts, or into `matchEnd` once the status is genuinely
 * `"Completed"` — never rendered twice for the same instant, and always at
 * the SAME canonical second whichever label it ends up wearing (so a viewer
 * watching live never sees the number itself jump when the label resolves).
 *
 * `nowSeconds` no longer feeds any divider's position (that was the bug);
 * `hasGenuinePeriodEnd` and the toStarted/isCompleted branching below —
 * which decide whether a row exists at all and which label it gets — are
 * unchanged and still never consult it either.
 */
const CANONICAL_PERIOD_END_SECONDS: Record<PeriodKey, number> = {
  "1": REGULATION_FIRST_HALF_SECONDS,
  "2": REGULATION_SECOND_HALF_SECONDS,
  "3": EXTRA_FIRST_HALF_END_SECONDS,
  "4": EXTRA_SECOND_HALF_END_SECONDS,
};

/**
 * A period's `.end` only counts as a genuine boundary when it is strictly
 * after that SAME period's own `.start` (PMA-COCK-156, PROD INCIDENT).
 * `should_start` (`matches_new.py`) re-stamps `.start` on every clock
 * restart WITHOUT clearing a stale `.end` left over from a prior mid-period
 * stop, so a period that is genuinely still live can show `.end` BEFORE
 * its own `.start`. Trusting that stale `.end` made `buildBoundaryRows`
 * treat an in-progress first half as already over, rendering a bogus
 * halftime row on top of live play.
 */
const hasGenuinePeriodEnd = (
  periodTimestamps: NonNullable<Match["period_timestamps"]>,
  key: PeriodKey,
): boolean => {
  const entry = periodTimestamps[key];
  if (!entry?.end) return false;
  if (!entry.start) return true;
  const endMs = parseTimestampSafe(entry.end);
  const startMs = parseTimestampSafe(entry.start);
  if (!Number.isFinite(endMs) || !Number.isFinite(startMs)) return true;
  return endMs > startMs;
};

/**
 * Live status → the period it maps to, mirroring the backend's
 * `_status_period` mapping (`matches_new.py`) so the two layers agree on
 * exactly which period counts as "the match's current live period."
 */
const LIVE_STATUS_BY_PERIOD_KEY: Record<PeriodKey, string> = {
  "1": "Live_First_Half",
  "2": "Live_Second_Half",
  "3": "Live_Extra_First",
  "4": "Live_Extra_Second",
};

/**
 * True when `periodKey`'s period is BOTH the match's current live status
 * AND has a running clock (`currentPeriodStartTimestamp` set) — the signal
 * PMA-COCK-173 uses to ignore an otherwise-genuine `.end` (`.end > .start`,
 * so `hasGenuinePeriodEnd` above and PMA-COCK-156's guard both trust it):
 * a mid-period Stop stamps `.end` unconditionally, and a clock-mode resume
 * (as opposed to the Start button, which `$unset`s a stale `.end`
 * server-side) never clears `period_timestamps` at all, so a period that is
 * demonstrably still live and running can carry a wholly coherent-looking
 * but stale `.end`.
 */
export const isPeriodLiveAndRunning = (
  periodKey: PeriodKey,
  matchStatus: string | undefined,
  currentPeriodStartTimestamp: string | null | undefined,
): boolean => {
  if (!currentPeriodStartTimestamp) return false;
  return LIVE_STATUS_BY_PERIOD_KEY[periodKey] === matchStatus;
};

export const buildBoundaryRows = (
  periodTimestamps: Match["period_timestamps"] | undefined,
  matchStatus: string | undefined,
  // Kept for a stable call signature (the component still threads its own
  // `nowSeconds` prop through here) — no divider's position is derived from
  // it any more; see the doc comment above.
  _nowSeconds: number,
  /**
   * PMA-COCK-163 addendum (2026-09-07): period numbers with at least one
   * goal/card/substitution already attached, computed by the caller from
   * the same event props it renders. A period transition (`should_start`,
   * `matches_new.py`) always stamps the NEW period's `global_start_seconds`,
   * but only stamps `.start` when no clock was already running at the
   * instant of the transition (`current_period_start_timestamp` unset) — a
   * clock that was already ticking through the transition (e.g. resumed in
   * period 1, then the operator flips status to `Live_Second_Half` without
   * a fresh clock start) leaves period 2 with `global_start_seconds` but no
   * `.start`, and the prior period keeps no `.end` either. Dev incident:
   * `period_timestamps` `{"1": {global_start_seconds: 0, start: "..."}, "2":
   * {global_start_seconds: 2773}}` under `Live_Second_Half` rendered NO
   * halftime divider at all pre-fix. See `started` below.
   */
  periodsWithEvents?: ReadonlySet<number>,
  /**
   * PMA-COCK-173: threaded through so `fromEnded` can be forced false for a
   * period that is live and running (see `isPeriodLiveAndRunning` above).
   * Optional and appended last so every existing call site (positional,
   * omitting this arg) keeps its exact pre-existing behavior — the guard
   * `if (!currentPeriodStartTimestamp) return false;` makes `undefined`
   * behave identically to "not live and running."
   */
  currentPeriodStartTimestamp?: string | null,
): BoundaryRow[] => {
  if (!periodTimestamps) return [];
  const rows: BoundaryRow[] = [];

  const started: Partial<Record<PeriodKey, boolean>> = {};
  PERIOD_KEYS.forEach((key) => {
    const entry = periodTimestamps[key];
    started[key] =
      Boolean(entry?.start) ||
      entry?.global_start_seconds !== undefined ||
      Boolean(periodsWithEvents?.has(Number(key)));
  });

  if (started["1"]) {
    rows.push({
      kind: "boundary",
      id: "matchStart",
      boundaryKind: "matchStart",
      seconds: CANONICAL_START_SECONDS.FIRST_HALF ?? 0,
      period: 1,
    });
  }

  let lastPlayedKey: PeriodKey | null = null;
  PERIOD_KEYS.forEach((key) => {
    if (started[key]) lastPlayedKey = key;
  });

  const isCompleted = matchStatus === "Completed";

  BOUNDARY_TRANSITIONS.forEach(
    ({ from, to, boundaryKind, pendingBoundaryKind }) => {
      const toStarted = Boolean(started[to]);
      // PMA-COCK-173: an `.end` that is otherwise genuine (`.end > .start`,
      // so PMA-COCK-156's incoherent-window guard inside
      // `hasGenuinePeriodEnd` does not fire) is still forced false here
      // while `from`'s own period is live and running — see
      // `isPeriodLiveAndRunning`'s doc comment for the exact prod mechanism.
      const fromEnded =
        hasGenuinePeriodEnd(periodTimestamps, from) &&
        !isPeriodLiveAndRunning(from, matchStatus, currentPeriodStartTimestamp);
      // Render only once there is SOME signal the transition happened: an
      // explicit `.end` on `from` (PMA-COCK-156's guard, unchanged), OR `to`
      // demonstrably started by any of the broadened `started` signals above
      // (PMA-COCK-163 addendum) — being in period `to` at all is itself
      // proof `from` is over, even without its own stamped `.end`.
      if (!fromEnded && !toStarted) return;
      const canonicalSeconds =
        CANONICAL_START_SECONDS[PHASE_BY_PERIOD_KEY[to]] ?? 0;
      if (toStarted) {
        rows.push({
          kind: "boundary",
          id: boundaryKind,
          boundaryKind,
          seconds: canonicalSeconds,
          period: Number(to),
        });
        return;
      }
      // "to" hasn't started by any signal. A genuinely completed match means
      // this end IS the final whistle, not a pending transition — matchEnd
      // below covers it, skip here to avoid rendering both for the same
      // instant.
      if (isCompleted) return;
      const kind = pendingBoundaryKind ?? boundaryKind;
      rows.push({
        kind: "boundary",
        id: kind,
        boundaryKind: kind,
        // Same canonical second the resolved label would use once "to"
        // actually starts (see the doc comment's "no visible jump" note) —
        // grouped under `from`'s block since "to" doesn't exist yet.
        seconds: canonicalSeconds,
        period: Number(from),
      });
    },
  );

  // The last tracked period (4) has no "next period" transition entry above,
  // so its own pending-and-not-completed case (extra time played, outcome —
  // penalties or straight to full time — not yet known) needs the same
  // neutral treatment applied symmetrically here.
  if (
    lastPlayedKey === "4" &&
    hasGenuinePeriodEnd(periodTimestamps, "4") &&
    !isCompleted
  ) {
    rows.push({
      kind: "boundary",
      id: "extraTimeEnd",
      boundaryKind: "extraTimeEnd",
      seconds: CANONICAL_PERIOD_END_SECONDS["4"],
      period: 4,
    });
  }

  if (
    lastPlayedKey &&
    hasGenuinePeriodEnd(periodTimestamps, lastPlayedKey) &&
    isCompleted
  ) {
    rows.push({
      kind: "boundary",
      id: "matchEnd",
      boundaryKind: "matchEnd",
      seconds: CANONICAL_PERIOD_END_SECONDS[lastPlayedKey],
      period: Number(lastPlayedKey),
    });
  }

  return rows;
};

const BOUNDARY_LABELS: Record<BoundaryKind, { key: string; fallback: string }> =
  {
    matchStart: { key: "publicTimelineMatchStart", fallback: "Match start" },
    halftime: { key: "publicTimelineHalftime", fallback: "Half-time" },
    secondHalfEnd: {
      key: "publicTimelineSecondHalfEnd",
      fallback: "End of second half",
    },
    extraTimeStart: {
      key: "publicTimelineExtraTimeStart",
      fallback: "Start of extra time",
    },
    extraTimeHalftime: {
      key: "publicTimelineExtraTimeHalftime",
      fallback: "Extra-time half-time",
    },
    extraTimeEnd: {
      key: "publicTimelineExtraTimeEnd",
      fallback: "End of extra time",
    },
    matchEnd: { key: "publicTimelineMatchEnd", fallback: "Match end" },
  };

/** "47:00.001" → 2820.001 seconds. Tolerates a bare "MM:SS" too. */
export const parseMatchClockSeconds = (clock?: string | null): number => {
  if (!clock) return 0;
  const [mm, rest] = String(clock).split(":");
  const minutes = Number(mm);
  const seconds = parseFloat(rest ?? "0");
  if (!Number.isFinite(minutes) || !Number.isFinite(seconds)) return 0;
  return minutes * 60 + seconds;
};

/**
 * Minute label: 00:10 → 0', 6:10 → 6', 47:00 → 47'. The exact mark, no
 * `+1`. Previously used the broadcast "minute in progress" convention
 * (`floor(seconds / 60) + 1`) for event rows only, on the theory that a
 * goal at 45:30 "happened in the 46th minute in progress" — but the score
 * bar directly above this timeline shows the raw clock, and a client
 * booking a card at a clock reading of 06:10 saw the timeline label it
 * `7'`, contradicting the clock they were looking at. Dropping the `+1`
 * makes the label agree with the clock instead. Do NOT reintroduce a `+1`
 * here for "minute in progress" — that convention is exactly what caused
 * the contradiction. This also makes the formula identical to the
 * boundary-row rule below, so both names now share one implementation.
 */
export const formatMinuteFromSeconds = (seconds: number): string =>
  `${Math.floor(Math.max(0, seconds) / 60)}'`;

export const formatTimelineMinute = (clock?: string | null): string =>
  formatMinuteFromSeconds(parseMatchClockSeconds(clock));

/**
 * Boundary-mark minute — kept as a distinct exported name (kickoff is 0',
 * half-time 45', full time 90', end of extra time 120') even though it is
 * now literally the same function as `formatMinuteFromSeconds` above: call
 * sites read more clearly with a name that matches what they're labeling.
 */
export const formatBoundaryMinute = formatMinuteFromSeconds;

/**
 * The minimal shape every timed public row reduces to (goal / card / sub).
 */
export interface PublicTimedEvent {
  timestamp?: string | null;
  period?: number;
  match_clock?: string | null;
}

/**
 * Fallback period bracket when an event carries no `period` field of its
 * own — derived from which canonical half/extra-time window its elapsed
 * seconds falls in. Only a fallback: real events always carry `period`
 * (PMA-COCK-163), and this cannot distinguish a first-half stoppage-time
 * event from a second-half event at the same continuous elapsed second
 * (both numbering schemes share one clock) the way the real field does.
 */
const resolveFallbackPeriod = (seconds: number): number => {
  if (seconds < (CANONICAL_START_SECONDS.SECOND_HALF ?? Infinity)) return 1;
  if (seconds < (CANONICAL_START_SECONDS.FIRST_HALF_EXTRA_TIME ?? Infinity))
    return 2;
  if (seconds < (CANONICAL_START_SECONDS.SECOND_HALF_EXTRA_TIME ?? Infinity))
    return 3;
  return 4;
};

/**
 * Resolves a row's placement (`seconds`, for sorting), display label, and
 * `period` (the sort's primary key, PMA-COCK-163) for an event-like input
 * (goal/card/sub all reduce to the same three fields). Two
 * retroactive-reconstruction cases, checked in order:
 *
 * 1. Halftime-break membership (Bug 4): `event.timestamp` falls inside
 *    the wall-clock window between a period's persisted `end` and the
 *    next period's `start` — reuses the same boundary-row label
 *    (`logger.publicTimelineHalftime` / `publicTimelineExtraTimeHalftime`)
 *    and places the row at the same `seconds` as that boundary marker so
 *    it clusters visually right at the break.
 * 2. Stoppage-time notation (Bug 3): `reconstructStoppageElapsedSeconds`
 *    only returns a value when the persisted `match_clock` is sitting
 *    exactly on the period's regulation cap (the ambiguous case) —
 *    otherwise this falls through to the parsed `match_clock` unchanged,
 *    which is already correct for both normal in-play events and
 *    already-uncapped events from the going-forward stamping fix.
 *
 * Hoisted out of the component body (PMA-COCK-125) so the score bar's
 * roster status marks can label a substitution/card minute with the exact
 * same rule the timeline uses. Two surfaces on one screen disagreeing
 * about what minute a substitution happened reads as a bug, so they must
 * share this function rather than each parse `match_clock` their own way.
 *
 * `matchStatus`/`currentPeriodStartTimestamp` (PMA-COCK-173, both optional
 * so every pre-existing caller keeps its exact behavior) identify a period
 * that is live and running (`isPeriodLiveAndRunning`) so its halftime
 * window is skipped even when the underlying `.end` looks otherwise
 * genuine — mirrors `buildBoundaryRows`' identical `fromEnded` guard so the
 * divider and every event row agree on the same live period.
 */
export const resolvePublicEventTiming = (
  event: PublicTimedEvent,
  periodTimestamps: Match["period_timestamps"] | undefined,
  t: (key: string, fallback?: string) => string,
  matchStatus?: string,
  currentPeriodStartTimestamp?: string | null,
): { seconds: number; minuteLabel: string; period: number } => {
  const liveRunningPeriodKey = PERIOD_KEYS.find((key) =>
    isPeriodLiveAndRunning(key, matchStatus, currentPeriodStartTimestamp),
  );
  const skipEndKeys = liveRunningPeriodKey
    ? new Set<string>([liveRunningPeriodKey])
    : undefined;
  const halftimeKind = findHalftimeWindowForTimestamp(
    event.timestamp,
    periodTimestamps,
    skipEndKeys,
  );
  if (halftimeKind) {
    const label = BOUNDARY_LABELS[halftimeKind];
    const seconds =
      halftimeKind === "halftime"
        ? (CANONICAL_START_SECONDS.SECOND_HALF ?? 0)
        : (CANONICAL_START_SECONDS.SECOND_HALF_EXTRA_TIME ?? 0);
    // Belongs at the boundary between the period that just ended and the
    // one about to start — the same `period` `buildBoundaryRows` assigns
    // the matching divider, so the two sort adjacent regardless of this
    // event's own (backend-mapped-to-1) `period` field.
    const period = halftimeKind === "halftime" ? 2 : 4;
    return { seconds, minuteLabel: t(label.key, label.fallback), period };
  }

  const reconstructedSeconds = reconstructStoppageElapsedSeconds(
    event,
    periodTimestamps,
  );
  const elapsedSeconds =
    reconstructedSeconds ?? parseMatchClockSeconds(event.match_clock);
  const { baseMinutes, addedMinutes } = splitElapsedSecondsIntoMinutes(
    elapsedSeconds,
    event.period,
  );
  const period =
    typeof event.period === "number" && Number.isFinite(event.period)
      ? event.period
      : resolveFallbackPeriod(elapsedSeconds);
  return {
    seconds: elapsedSeconds,
    minuteLabel: formatMatchTime(baseMinutes, addedMinutes),
    period,
  };
};

const CHIP_CLASS = "inline-block w-2.5 h-3.5 rounded-sm";

export function PublicMatchTimeline({
  goals,
  cards,
  substitutions,
  formatGoalScorer,
  t,
  periodTimestamps,
  matchStatus,
  currentPeriodStartTimestamp,
  nowSeconds = 0,
  shootoutKicks = [],
  shootoutHomeScore = 0,
  shootoutAwayScore = 0,
  shootoutRound,
}: PublicMatchTimelineProps) {
  const resolveEventTiming = (event: PublicTimedEvent) =>
    resolvePublicEventTiming(
      event,
      periodTimestamps,
      t,
      matchStatus,
      currentPeriodStartTimestamp,
    );

  const rows = useMemo<TimelineRow[]>(() => {
    const collected: TimelineRow[] = [];

    (["home", "away"] as const).forEach((side) => {
      goals[side].forEach((g, index) => {
        const timing = resolveEventTiming(g);
        collected.push({
          kind: "goal",
          id: g._id ?? g.client_id ?? `goal-${side}-${index}`,
          side,
          seconds: timing.seconds,
          period: timing.period,
          label: formatGoalScorer(g),
          minuteLabel: timing.minuteLabel,
          penalty: isPenaltyGoal(g),
        });
      });
      cards[side].forEach((entry) => {
        const timing = resolveEventTiming({
          timestamp: entry.timestamp,
          period: entry.period,
          match_clock: entry.minute,
        });
        collected.push({
          kind: "card",
          id: entry.id,
          side,
          seconds: timing.seconds,
          period: timing.period,
          entry,
          minuteLabel: timing.minuteLabel,
        });
      });
      substitutions[side].forEach((entry) => {
        const timing = resolveEventTiming({
          timestamp: entry.timestamp,
          period: entry.period,
          match_clock: entry.minute,
        });
        collected.push({
          kind: "sub",
          id: entry.id,
          side,
          seconds: timing.seconds,
          period: timing.period,
          entry,
          minuteLabel: timing.minuteLabel,
        });
      });
    });

    // PMA-COCK-163 addendum: periods with at least one event attached, so
    // `buildBoundaryRows` can treat a period as "started" even when neither
    // its own `.start` nor `global_start_seconds` is stamped yet (see that
    // function's doc comment for the dev incident this covers).
    const periodsWithEvents = new Set<number>();
    (["home", "away"] as const).forEach((side) => {
      goals[side].forEach((g) => periodsWithEvents.add(g.period));
      cards[side].forEach((entry) => periodsWithEvents.add(entry.period));
      substitutions[side].forEach((entry) =>
        periodsWithEvents.add(entry.period),
      );
    });

    collected.push(
      ...buildBoundaryRows(
        periodTimestamps,
        matchStatus,
        nowSeconds,
        periodsWithEvents,
        currentPeriodStartTimestamp,
      ),
    );

    // Newest first — the most recent moment is at the top. `period` sorts
    // FIRST (PMA-COCK-163): a first-half stoppage-time event and a
    // second-half event can carry the identical continuous elapsed second
    // (both halves share one clock numbering), so sorting on `seconds`
    // alone let a stoppage-time event drift into the wrong half's visual
    // block. Within a period, ties (an event and a boundary landing on the
    // same second, e.g. a card thrown right as the ref blows for half-time)
    // put the boundary above the event: the boundary is the instant's
    // resolving fact — "this is what that second amounted to" — so it reads
    // as the more recent of the two even though nothing actually separates
    // them in time.
    return collected.sort((a, b) => {
      if (b.period !== a.period) return b.period - a.period;
      if (b.seconds !== a.seconds) return b.seconds - a.seconds;
      const rank = (row: TimelineRow) => (row.kind === "boundary" ? 1 : 0);
      return rank(b) - rank(a);
    });
  }, [
    goals,
    cards,
    substitutions,
    formatGoalScorer,
    t,
    periodTimestamps,
    matchStatus,
    currentPeriodStartTimestamp,
    nowSeconds,
  ]);

  const renderCardGlyphs = (kind: PublicCardKind) => {
    if (kind === "secondYellow") {
      return (
        <span
          className="inline-flex shrink-0 items-center gap-0.5"
          role="img"
          aria-label={t(
            "publicCardSecondYellowAria",
            "Second yellow card and red card",
          )}
        >
          <span className={`${CHIP_CLASS} bg-yellow-400`} />
          <span className={`${CHIP_CLASS} bg-yellow-400`} />
          <span className={`${CHIP_CLASS} bg-red-500`} />
        </span>
      );
    }
    return (
      <span
        role="img"
        aria-label={
          kind === "red"
            ? t("publicCardRedAria", "Red card")
            : t("publicCardYellowAria", "Yellow card")
        }
        className={`${CHIP_CLASS} shrink-0 ${
          kind === "red" ? "bg-red-500" : "bg-yellow-400"
        }`}
      />
    );
  };

  const renderRowBody = (row: GoalRow | CardRow | SubRow) => {
    const isHome = row.side === "home";
    // Content hugs the spine: home rows are right-aligned, away rows left.
    const align = isHome ? "items-end text-right" : "items-start text-left";
    const inline = isHome ? "flex-row-reverse" : "flex-row";

    if (row.kind === "goal") {
      return (
        <span
          data-testid={`public-goal-${row.id}`}
          data-side={row.side}
          data-penalty={row.penalty ? "true" : "false"}
          className={`flex min-w-0 ${inline} items-start gap-1.5 text-xs font-medium text-white sm:text-sm`}
        >
          <span
            role="img"
            aria-label={t("publicGoalAria", "Goal")}
            className="shrink-0 leading-5"
          >
            ⚽
          </span>
          {/* "(P)" for a scored penalty is baked into `row.label` itself —
              `formatGoalScorer` (useLiveScore.ts, PMA-COCK-195) appends it
              the same way it already appends " (AG)" for an own goal, so
              there is exactly one place a scorer suffix can come from.
              `data-penalty` above is a locale-independent test hook for the
              same fact, not a second rendering of it. */}
          <span className={`min-w-0 break-words ${align}`}>{row.label}</span>
        </span>
      );
    }

    if (row.kind === "card") {
      return (
        <span
          data-testid={`public-card-${row.id}`}
          data-card-kind={row.entry.kind}
          data-side={row.side}
          className={`flex min-w-0 ${inline} items-start gap-1.5 text-xs font-medium text-white sm:text-sm`}
        >
          {renderCardGlyphs(row.entry.kind)}
          <span className={`min-w-0 break-words ${align}`}>
            {row.entry.playerLabel}
          </span>
        </span>
      );
    }

    return (
      <span
        data-testid={`public-sub-${row.id}`}
        data-side={row.side}
        className={`flex min-w-0 flex-col gap-0.5 text-xs font-medium text-slate-300 sm:text-sm ${align}`}
      >
        <span className={`flex min-w-0 ${inline} items-start gap-1.5`}>
          <span
            role="img"
            aria-label={t("publicSubstitutionOnAria", "Substituted on")}
            className="shrink-0 leading-5 text-emerald-400"
          >
            ▲
          </span>
          <span
            data-testid="public-sub-on-label"
            className="min-w-0 break-words text-emerald-400"
          >
            {row.entry.onLabel}
          </span>
        </span>
        <span className={`flex min-w-0 ${inline} items-start gap-1.5`}>
          <span
            role="img"
            aria-label={t("publicSubstitutionOffAria", "Substituted off")}
            className="shrink-0 leading-5 text-red-400"
          >
            ▼
          </span>
          <span
            data-testid="public-sub-off-label"
            className="min-w-0 break-words text-red-400"
          >
            {row.entry.offLabel}
          </span>
        </span>
      </span>
    );
  };

  if (rows.length === 0 && shootoutKicks.length === 0) return null;

  const OUTCOME_LABEL: Record<
    PublicShootoutKickEntry["outcome"],
    [string, string]
  > = {
    Goal: ["publicTimelineShootoutOutcomeScored", "Scored"],
    Saved: ["publicTimelineShootoutOutcomeSaved", "Saved"],
    Missed: ["publicTimelineShootoutOutcomeMissed", "Missed"],
  };

  return (
    <section
      className="mt-4 rounded-xl border border-surface-dark-border bg-surface-dark-card p-4 sm:p-6"
      data-testid="public-match-timeline"
    >
      <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-200">
        {t("publicTimelineTitle", "Match detail")}
      </h3>

      {/* Scroll container: caps the section height so the timeline never
          pushes the analytics below the fold on a phone, keeps its own
          scrollbar (overscroll-contain stops the page from scroll-chaining),
          and fades the clipped edges so it reads as "more above/below". */}
      <div className="relative">
        <div
          className="max-h-[20rem] overflow-y-auto overscroll-contain pr-1 sm:max-h-[26rem]"
          data-testid="public-timeline-scroll"
        >
          {shootoutKicks.length > 0 && (
            /* PMA-COCK-164 / PMA-PUB-017 (365scores reference restyle,
               2026-09-16 client wave): the shootout, kick by kick — its own
               block above the minute-ordered spine, ordered by kick
               sequence (not time: every kick shares the same
               PENALTIES-phase clock reading, so it can never be placed on
               the spine below). A shootout only ever happens at the very
               end of a match, so "above everything else" is always the
               correct chronological position too. Restyled, not rebuilt:
               `derivePublicShootoutRows` already supplies `order`, `side`,
               `playerLabel` and `outcome` — only the presentation changes.
               Own centre spine (mirrors the main `<ol>`'s below) so the
               per-kick order badge has a line to sit on. */
            <ol className="relative m-0 mb-2 list-none space-y-0.5 border-b border-slate-700/60 p-0 pb-2">
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-slate-600/60"
              />
              <li
                data-testid="timeline-shootout-divider"
                data-row-kind="boundary"
                className="flex items-center gap-2 py-2"
              >
                <span
                  aria-hidden="true"
                  className="h-px flex-1 bg-slate-700/60"
                />
                <span
                  data-testid="timeline-shootout-score"
                  className="relative z-10 flex shrink-0 items-center gap-1.5 rounded-full border border-slate-700/60 bg-surface-dark-card px-2.5 py-1 text-[0.65rem] font-semibold uppercase tracking-wide text-slate-200 sm:text-[0.7rem]"
                >
                  {t(
                    "publicTimelineShootoutScore",
                    "Penalties {{home}} - {{away}}",
                  )
                    .split("{{home}}")
                    .join(String(shootoutHomeScore))
                    .split("{{away}}")
                    .join(String(shootoutAwayScore))}
                  {/* PMA-COCK-201: same keys and wording the cockpit panel
                      renders for the identical `phase`/`round` pair — see
                      the `shootoutRound` prop doc comment above. */}
                  {shootoutRound && (
                    <>
                      <span aria-hidden="true">·</span>
                      <span
                        data-testid="shootout-round-label"
                        data-round-phase={shootoutRound.phase}
                      >
                        {shootoutRound.phase === "suddenDeath"
                          ? t(
                              "shootoutSuddenDeathLabel",
                              "Sudden death — Round {{round}}",
                            )
                              .split("{{round}}")
                              .join(String(shootoutRound.round))
                          : t("shootoutRoundLabel", "Round {{round}} of 5")
                              .split("{{round}}")
                              .join(String(shootoutRound.round))}
                      </span>
                    </>
                  )}
                </span>
                <span
                  aria-hidden="true"
                  className="h-px flex-1 bg-slate-700/60"
                />
              </li>
              {shootoutKicks.map((kick) => {
                const [labelKey, labelFallback] = OUTCOME_LABEL[kick.outcome];
                const scored = kick.outcome === "Goal";
                const isHome = kick.side === "home";
                const align = isHome
                  ? "items-end text-right"
                  : "items-start text-left";
                const inline = isHome ? "flex-row-reverse" : "flex-row";
                const kickBody = (
                  <span
                    className={`flex min-w-0 ${inline} items-center gap-1 text-xs font-medium text-white sm:text-sm`}
                  >
                    <span aria-hidden="true" className="shrink-0 leading-5">
                      ⚽
                    </span>
                    <span
                      aria-hidden="true"
                      className={`shrink-0 text-sm font-bold leading-none ${
                        scored ? "text-emerald-400" : "text-rose-400"
                      }`}
                    >
                      {scored ? "✓" : "✕"}
                    </span>
                    <span className={`min-w-0 truncate ${align}`}>
                      {kick.playerLabel}
                    </span>
                    {/* Scored/Saved/Missed stays the accessible label — the
                        existing i18n keys and screen-reader behaviour
                        survive the visual restyle to icons. */}
                    <span className="sr-only">
                      {t(labelKey, labelFallback)}
                    </span>
                  </span>
                );
                return (
                  <li
                    key={kick.key}
                    data-testid={`timeline-shootout-kick-${kick.order}`}
                    data-row-kind="shootout-kick"
                    data-side={kick.side}
                    data-outcome={kick.outcome}
                    className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-x-2 py-1 sm:gap-x-4"
                  >
                    <div className="flex min-w-0 justify-end">
                      {isHome ? kickBody : null}
                    </div>
                    <div className="flex w-8 shrink-0 justify-center">
                      <span className="relative z-10 rounded-full bg-surface-dark-card px-1.5 py-0.5 text-[0.65rem] font-semibold tabular-nums text-slate-400 sm:text-xs">
                        {kick.order}
                      </span>
                    </div>
                    <div className="flex min-w-0 justify-start">
                      {!isHome ? kickBody : null}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
          {rows.length > 0 && (
            <ol className="relative m-0 list-none p-0">
              {/* Center spine */}
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-slate-600/60"
              />
              {rows.map((row) => {
                const key = `${row.kind}-${row.id}`;

                if (row.kind === "boundary") {
                  const label = BOUNDARY_LABELS[row.boundaryKind];
                  return (
                    <li
                      key={key}
                      data-testid={`public-timeline-boundary-${row.boundaryKind}`}
                      data-row-kind="boundary"
                      data-boundary-kind={row.boundaryKind}
                      className="flex items-center gap-2 py-2"
                    >
                      {/* Structural context, not content — a plain divider
                        with a muted label rather than a colored/iconed row,
                        so it never competes with goals and cards for
                        attention. Centered so it lands on the spine, same
                        z-10-over-bg masking trick as the minute chip below. */}
                      <span
                        aria-hidden="true"
                        className="h-px flex-1 bg-slate-700/60"
                      />
                      <span className="relative z-10 flex shrink-0 items-center gap-1.5 rounded-full border border-slate-700/60 bg-surface-dark-card px-2.5 py-1 text-[0.65rem] font-semibold uppercase tracking-wide text-slate-400 sm:text-[0.7rem]">
                        <span className="tabular-nums text-slate-300">
                          {formatBoundaryMinute(row.seconds)}
                        </span>
                        <span aria-hidden="true">·</span>
                        <span>{t(label.key, label.fallback)}</span>
                      </span>
                      <span
                        aria-hidden="true"
                        className="h-px flex-1 bg-slate-700/60"
                      />
                    </li>
                  );
                }

                return (
                  <li
                    key={key}
                    data-testid={`public-timeline-row-${row.id}`}
                    data-row-kind={row.kind}
                    data-side={row.side}
                    className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-start gap-x-2 py-2 sm:gap-x-4"
                  >
                    <div className="flex min-w-0 justify-end">
                      {row.side === "home" ? renderRowBody(row) : null}
                    </div>
                    <div className="flex w-10 shrink-0 justify-center pt-0.5 sm:w-12">
                      {/* Card-coloured background so the chip masks the spine it
                        sits on, the way the reference design reads. `relative
                        z-10` is load-bearing: the spine is absolutely positioned
                        and would otherwise paint ABOVE this static background
                        (positioned elements paint after in-flow content even at
                        `z-index: auto`), drawing a 1px line straight through the
                        minute digits. */}
                      <span className="relative z-10 rounded-full bg-surface-dark-card px-1.5 py-0.5 text-[0.68rem] font-semibold tabular-nums text-slate-300 sm:text-xs">
                        {row.minuteLabel}
                      </span>
                    </div>
                    <div className="flex min-w-0 justify-start">
                      {row.side === "away" ? renderRowBody(row) : null}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 h-4 bg-gradient-to-b from-surface-dark-card to-transparent"
        />
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 bottom-0 h-4 bg-gradient-to-t from-surface-dark-card to-transparent"
        />
      </div>
    </section>
  );
}

export default PublicMatchTimeline;
